import React, { useState } from 'react';
import { useApi, api } from '../hooks/useApi';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { SkeletonList } from '../components/SkeletonCard';
import useMobile from '../hooks/useMobile';
import { useTheme } from '../context/ThemeContext';

const PRIORITIES = { high: { label: 'Viktig', color: '#F7605C', bg: '#F7605C18' }, normal: { label: 'Normal', color: '#7C6FF7', bg: '#7C6FF718' }, low: { label: 'Lav', color: 'var(--txl)', bg: 'var(--bg2)' } };

export default function TodoPage() {
  const isMobile = useMobile();
  const { dark } = useTheme();
  const { members } = useAuth();
  const { data: todos, loading, refresh } = useApi('/api/todos');
  const toast = useToast();
  const [form, setForm] = useState({ title: '', priority: 'normal', assigned_to: '', tag: '', due_date: '' });
  const [filter, setFilter] = useState('alle');
  const [showForm, setShowForm] = useState(false);

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const add = async e => {
    e.preventDefault();
    if (!form.title.trim()) return;
    await api.post('/api/todos', form);
    setForm({ title: '', priority: 'normal', assigned_to: '', tag: '', due_date: '' });
    setShowForm(false);
    refresh();
    toast.success('Oppgave lagt til');
  };

  const toggle = async todo => {
    await api.put(`/api/todos/${todo.id}`, { done: !todo.done });
    refresh();
    if (!todo.done) toast.success('Oppgave fullført! 🎉');
  };

  const remove = async id => {
    await api.delete(`/api/todos/${id}`);
    refresh();
    toast.info('Oppgave slettet');
  };

  const filtered = (todos || []).filter(t => {
    if (filter === 'aktive') return !t.done;
    if (filter === 'ferdig') return t.done;
    if (filter === 'viktig') return t.priority === 'high' && !t.done;
    return true;
  });

  const PCOLOR = { high:'#E5534B', normal:'#7C6FF7', low:'#888' };
  const pageBg = dark ? '#0A0818' : '#EDE8FF';
  const txt    = dark ? '#E8E8F8' : '#1A1A35';
  const sub    = dark ? '#9090B8' : '#888';
  const border = dark ? 'rgba(255,255,255,.07)' : 'rgba(124,111,247,0.12)';

  if (isMobile) return (
    <div style={{ background:pageBg, minHeight:'100%', fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
      {/* Gradient Hero Header */}
      <div style={{
        background: dark
          ? 'linear-gradient(145deg, #2D1B69 0%, #1A1245 55%, #0D0B1E 100%)'
          : 'linear-gradient(145deg, #7C6FF7 0%, #9358E8 45%, #5E4AE3 100%)',
        padding: '52px 20px 24px',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{ position:'absolute', top:-40, right:-40, width:160, height:160, borderRadius:'50%', background:'rgba(255,255,255,0.06)', pointerEvents:'none' }}/>
        <div style={{ position:'absolute', bottom:-60, left:-20, width:140, height:140, borderRadius:'50%', background:'rgba(255,255,255,0.04)', pointerEvents:'none' }}/>
        <div style={{ position:'relative', zIndex:1, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <div>
            <div style={{ fontSize:13, color:'rgba(255,255,255,0.65)', fontWeight:500, marginBottom:2 }}>Famora</div>
            <div style={{ fontSize:24, fontWeight:800, color:'#fff' }}>Oppgaver</div>
          </div>
          <button onClick={() => setShowForm(v=>!v)} style={{ background:'rgba(255,255,255,0.15)', backdropFilter:'blur(10px)', color:'#fff', border:'1px solid rgba(255,255,255,0.25)', borderRadius:12, padding:'8px 16px', fontWeight:700, fontSize:14, cursor:'pointer' }}>
            {showForm ? 'Avbryt' : '+ Ny'}
          </button>
        </div>
        {/* Filter chips in hero */}
        <div style={{ position:'relative', zIndex:1, display:'flex', gap:6, overflowX:'auto', scrollbarWidth:'none', marginTop:16 }}>
          {['alle','aktive','viktig','ferdig'].map(f => (
            <button key={f} onClick={() => setFilter(f)} style={{ padding:'7px 14px', borderRadius:20, border:'none', background: filter===f?'rgba(255,255,255,0.25)':'rgba(255,255,255,0.1)', color:'#fff', fontWeight:700, fontSize:13, cursor:'pointer', whiteSpace:'nowrap', flexShrink:0 }}>
              {f.charAt(0).toUpperCase()+f.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Skjema */}
      {showForm && (
        <form onSubmit={add} className="glass-card" style={{ margin:'12px 14px', padding:14, display:'flex', flexDirection:'column', gap:10 }}>
          <input style={{ padding:'12px 14px', borderRadius:12, border:'1px solid '+(dark?'rgba(255,255,255,0.12)':'rgba(124,111,247,0.2)'), background: dark?'rgba(255,255,255,0.08)':'rgba(255,255,255,0.7)', color:txt, fontSize:14, fontFamily:'inherit', outline:'none' }} placeholder="Hva skal gjøres?" value={form.title} onChange={set('title')} autoFocus required />
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
            <select style={{ padding:'11px 12px', borderRadius:12, border:'1px solid '+(dark?'rgba(255,255,255,0.12)':'rgba(124,111,247,0.2)'), background: dark?'rgba(255,255,255,0.08)':'rgba(255,255,255,0.7)', color:txt, fontSize:13, fontFamily:'inherit', outline:'none' }} value={form.priority} onChange={set('priority')}>
              <option value="high">🔴 Viktig</option>
              <option value="normal">🟣 Normal</option>
              <option value="low">⚪ Lav</option>
            </select>
            <select style={{ padding:'11px 12px', borderRadius:12, border:'1px solid '+(dark?'rgba(255,255,255,0.12)':'rgba(124,111,247,0.2)'), background: dark?'rgba(255,255,255,0.08)':'rgba(255,255,255,0.7)', color:txt, fontSize:13, fontFamily:'inherit', outline:'none' }} value={form.assigned_to} onChange={set('assigned_to')}>
              <option value="">Ingen tildelt</option>
              {members.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
          </div>
          <input type="date" style={{ padding:'11px 14px', borderRadius:12, border:'1px solid '+(dark?'rgba(255,255,255,0.12)':'rgba(124,111,247,0.2)'), background: dark?'rgba(255,255,255,0.08)':'rgba(255,255,255,0.7)', color:txt, fontSize:13, fontFamily:'inherit', outline:'none' }} value={form.due_date} onChange={set('due_date')} />
          <button type="submit" style={{ background:'linear-gradient(135deg,#7C6FF7,#9358E8)', color:'#fff', border:'none', borderRadius:12, padding:'13px', fontWeight:700, fontSize:15, cursor:'pointer' }}>Legg til oppgave</button>
        </form>
      )}

      {/* Liste */}
      <div style={{ padding:'12px 14px 0' }}>
        {loading && !todos && <SkeletonList count={4} style={{ marginBottom: 16 }} />}
        {!loading && filtered.length === 0 && (
          <div style={{ textAlign:'center', padding:'60px 20px', color:sub }}>
            <div style={{ fontSize:48, marginBottom:12 }}>✅</div>
            <div style={{ fontSize:16, fontWeight:700 }}>Ingen oppgaver her</div>
          </div>
        )}
        <div className="glass-card" style={{ padding:0, overflow:'hidden', marginBottom:12 }}>
          {filtered.filter(t => !t.done).map((todo, i, arr) => (
            <div key={todo.id} style={{ padding:'14px 16px', display:'flex', alignItems:'flex-start', gap:14, borderTop: i>0?'1px solid '+border:'none' }}>
              <button onClick={() => toggle(todo)} style={{ width:26, height:26, borderRadius:8, border:`2.5px solid ${todo.done?'#34D399':PCOLOR[todo.priority||'normal']}`, background:todo.done?'#34D399':'transparent', cursor:'pointer', flexShrink:0, display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontSize:14, fontWeight:800, marginTop:1 }}>
                {todo.done && '✓'}
              </button>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontSize:15, fontWeight:600, color:txt, marginBottom:4 }}>{todo.title}</div>
                <div style={{ display:'flex', flexWrap:'wrap', gap:5 }}>
                  {todo.priority==='high' && <span style={{ fontSize:11, fontWeight:700, color:'#E5534B', background:'#FEE2E218', padding:'2px 8px', borderRadius:20 }}>Viktig</span>}
                  {todo.assigned_name && <span style={{ fontSize:12, color:sub }}>→ {todo.assigned_name}</span>}
                  {todo.due_date && <span style={{ fontSize:12, color:sub }}>📅 {todo.due_date}</span>}
                  {todo.tag && <span style={{ fontSize:11, background:dark?'rgba(255,255,255,0.1)':'rgba(124,111,247,0.1)', color:txt, padding:'2px 8px', borderRadius:20, fontWeight:600 }}>{todo.tag}</span>}
                </div>
              </div>
              <button onClick={() => remove(todo.id)} style={{ background:'none', border:'none', color:sub, cursor:'pointer', fontSize:18, flexShrink:0, padding:'2px' }}>✕</button>
            </div>
          ))}
          {filtered.filter(t => !t.done).length === 0 && filtered.filter(t => t.done).length === 0 && null}
        </div>
        {filtered.filter(t => t.done).length > 0 && (
          <div className="glass-card" style={{ padding:0, overflow:'hidden', opacity:0.7 }}>
            <div style={{ padding:'10px 16px', fontSize:11, fontWeight:700, color:sub, textTransform:'uppercase', letterSpacing:1 }}>Fullførte</div>
            {filtered.filter(t => t.done).map((todo, i) => (
              <div key={todo.id} style={{ padding:'12px 16px', display:'flex', alignItems:'flex-start', gap:14, borderTop:'1px solid '+border }}>
                <button onClick={() => toggle(todo)} style={{ width:26, height:26, borderRadius:8, border:'2.5px solid #34D399', background:'#34D399', cursor:'pointer', flexShrink:0, display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontSize:14, fontWeight:800, marginTop:1 }}>✓</button>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:15, fontWeight:600, color:sub, textDecoration:'line-through' }}>{todo.title}</div>
                </div>
                <button onClick={() => remove(todo.id)} style={{ background:'none', border:'none', color:sub, cursor:'pointer', fontSize:18, flexShrink:0 }}>✕</button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div>
      <div style={s.topRow}>
        <div style={s.pills}>
          {['alle','aktive','viktig','ferdig'].map(f => (
            <button key={f} style={{...s.pill, ...(filter===f?s.pillActive:{})}} onClick={() => setFilter(f)}>
              {f.charAt(0).toUpperCase()+f.slice(1)}
            </button>
          ))}
        </div>
        <button style={s.addBtn} onClick={() => setShowForm(v => !v)}>+ Ny oppgave</button>
      </div>

      {showForm && (
        <form onSubmit={add} style={s.form}>
          <input style={s.inp} placeholder="Hva skal gjøres?" value={form.title} onChange={set('title')} autoFocus required />
          <div style={s.formRow}>
            <select style={s.sel} value={form.priority} onChange={set('priority')}>
              <option value="high">Viktig</option>
              <option value="normal">Normal</option>
              <option value="low">Lav</option>
            </select>
            <select style={s.sel} value={form.assigned_to} onChange={set('assigned_to')}>
              <option value="">Ingen tildelt</option>
              {members.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
            <input style={s.inp} type="date" value={form.due_date} onChange={set('due_date')} />
            <input style={s.inp} placeholder="Tag (f.eks. Hjem)" value={form.tag} onChange={set('tag')} />
          </div>
          <div style={{display:'flex',gap:8}}>
            <button style={s.saveBtn} type="submit">Legg til</button>
            <button style={s.cancelBtn} type="button" onClick={() => setShowForm(false)}>Avbryt</button>
          </div>
        </form>
      )}

      <div style={s.list}>
        {filtered.length === 0 && <div style={s.empty}>Ingen oppgaver her 🎉</div>}
        {filtered.map(todo => {
          const p = PRIORITIES[todo.priority] || PRIORITIES.normal;
          return (
            <div key={todo.id} style={{...s.item, opacity: todo.done ? 0.55 : 1}}>
              <button style={{...s.check, ...(todo.done ? s.checkDone : {})}} onClick={() => toggle(todo)}>
                {todo.done && '✓'}
              </button>
              <div style={s.itemBody}>
                <div style={{...s.itemTitle, textDecoration: todo.done ? 'line-through' : 'none'}}>{todo.title}</div>
                <div style={s.itemMeta}>
                  <span style={{...s.tag, background: p.bg, color: p.color}}>{p.label}</span>
                  {todo.tag && <span style={{...s.tag, background:'#34D39918',color:'#065F46'}}>{todo.tag}</span>}
                  {todo.assigned_name && <span style={s.who}>→ {todo.assigned_name}</span>}
                  {todo.due_date && <span style={s.due}>📅 {todo.due_date}</span>}
                </div>
              </div>
              <button style={s.del} onClick={() => remove(todo.id)}>✕</button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const s = {
  topRow:   { display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16, flexWrap:'wrap', gap:10 },
  pills:    { display:'flex', gap:6 },
  pill:     { padding:'6px 14px', borderRadius:20, border:'1px solid var(--bdr)', background:'var(--card)', fontSize:13, fontWeight:600, cursor:'pointer', color:'var(--txm)' },
  pillActive:{ background:'#7C6FF7', borderColor:'#7C6FF7', color:'#fff' },
  addBtn:   { padding:'8px 18px', borderRadius:10, border:'none', background:'#7C6FF7', color:'#fff', fontWeight:700, cursor:'pointer', fontSize:14 },
  form:     { background:'var(--card)', borderRadius:14, padding:16, border:'1px solid var(--bdr)', marginBottom:16, display:'flex', flexDirection:'column', gap:10 },
  formRow:  { display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(160px,1fr))', gap:8 },
  inp:      { padding:'9px 12px', borderRadius:9, border:'1px solid var(--input-bdr)', fontFamily:'inherit', fontSize:13, color:'var(--input-txt)', outline:'none', width:'100%', background:'var(--input-bg)' },
  sel:      { padding:'9px 12px', borderRadius:9, border:'1px solid var(--input-bdr)', fontFamily:'inherit', fontSize:13, color:'var(--input-txt)', outline:'none', background:'var(--input-bg)' },
  saveBtn:  { padding:'9px 20px', borderRadius:9, border:'none', background:'#7C6FF7', color:'#fff', fontWeight:700, cursor:'pointer', fontSize:13 },
  cancelBtn:{ padding:'9px 20px', borderRadius:9, border:'1px solid var(--bdr)', background:'var(--card)', color:'var(--txm)', fontWeight:600, cursor:'pointer', fontSize:13 },
  list:     { display:'flex', flexDirection:'column', gap:8 },
  empty:    { textAlign:'center', padding:40, color:'var(--txl)', fontSize:15 },
  item:     { display:'flex', alignItems:'flex-start', gap:12, padding:'12px 14px', background:'var(--card)', borderRadius:12, border:'1px solid var(--bdr)' },
  check:    { width:22, height:22, borderRadius:7, border:'2px solid var(--bdr)', background:'var(--card)', cursor:'pointer', flexShrink:0, display:'flex', alignItems:'center', justifyContent:'center', fontSize:12, fontWeight:700, color:'#fff', marginTop:1 },
  checkDone:{ background:'#34D399', borderColor:'#34D399' },
  itemBody: { flex:1 },
  itemTitle:{ fontSize:14, fontWeight:600, color:'var(--txt)', marginBottom:5 },
  itemMeta: { display:'flex', flexWrap:'wrap', gap:6, alignItems:'center' },
  tag:      { fontSize:11, fontWeight:700, padding:'2px 8px', borderRadius:20 },
  who:      { fontSize:12, color:'var(--txl)' },
  due:      { fontSize:12, color:'var(--txl)' },
  del:      { background:'none', border:'none', color:'var(--txl)', cursor:'pointer', fontSize:16, padding:'0 4px', flexShrink:0 },
};
